import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaChevronRight, FaHome, FaMoneyBillWave, FaSearch } from 'react-icons/fa';
import Button from '../components/common/Button';
import Heading from '../components/common/Heading';
import Section from '../components/common/Section';
import {
  createCoursePaymentCode,
  fetchRegistrationCoursesForPayment,
  fetchRegistrationPaymentStatus
} from '../services/registrationService';

const FINAL_PAYMENT_STATUSES = ['processed', 'completed'];
const STOP_POLLING_PAYMENT_STATUSES = [...FINAL_PAYMENT_STATUSES, 'expired', 'cancelled'];

const formatMoney = (amount) => `NLe ${Number(amount).toLocaleString('en')}`;

const CoursePaymentPage = () => {
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registration, setRegistration] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [paymentCode, setPaymentCode] = useState(null);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [creatingPaymentCode, setCreatingPaymentCode] = useState(false);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');

  const unpaidCourses = useMemo(
    () => courses.filter((course) => course.status === 'unpaid'),
    [courses]
  );

  const selectedTotal = unpaidCourses
    .filter((course) => selectedCourseIds.includes(course.id))
    .reduce((total, course) => total + Number(course.amount), 0);

  const paymentStatus = paymentCode?.status || 'pending';
  const paymentConfirmed = FINAL_PAYMENT_STATUSES.includes(paymentStatus);

  const loadRegistrationCourses = async (event) => {
    event?.preventDefault();

    const normalizedRegistrationNumber = registrationNumber.trim().toUpperCase();

    if (!normalizedRegistrationNumber) {
      setError('Enter your registration ID.');
      return;
    }

    setLoadingRegistration(true);
    setError('');
    setStatusError('');
    setPaymentCode(null);
    setSelectedCourseIds([]);

    try {
      const data = await fetchRegistrationCoursesForPayment(normalizedRegistrationNumber);
      setRegistration(data.registration);
      setCourses(data.courses || []);
      setRegistrationNumber(normalizedRegistrationNumber);
    } catch (requestError) {
      setRegistration(null);
      setCourses([]);
      setError(requestError.response?.data?.message || 'Unable to find registration.');
    } finally {
      setLoadingRegistration(false);
    }
  };

  const toggleCourse = (courseId) => {
    setPaymentCode(null);
    setStatusError('');
    setSelectedCourseIds((currentIds) => (
      currentIds.includes(courseId)
        ? currentIds.filter((id) => id !== courseId)
        : [...currentIds, courseId]
    ));
  };

  const handleCreatePaymentCode = async () => {
    if (!registration?.registration_number) {
      setError('Look up your registration ID first.');
      return;
    }

    if (selectedCourseIds.length === 0) {
      setError('Select at least one unpaid course.');
      return;
    }

    setCreatingPaymentCode(true);
    setError('');
    setStatusError('');

    try {
      const payment = await createCoursePaymentCode(registration.registration_number, selectedCourseIds);
      setPaymentCode(payment);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to create payment code.');
    } finally {
      setCreatingPaymentCode(false);
    }
  };

  useEffect(() => {
    if (!paymentCode?.id || STOP_POLLING_PAYMENT_STATUSES.includes(paymentCode.status)) {
      return undefined;
    }

    let isActive = true;

    const pollPaymentStatus = async () => {
      try {
        const payment = await fetchRegistrationPaymentStatus(paymentCode.id);

        if (!isActive) {
          return;
        }

        setPaymentCode(payment);
        setStatusError('');

        if (FINAL_PAYMENT_STATUSES.includes(payment.status) && registration?.registration_number) {
          const data = await fetchRegistrationCoursesForPayment(registration.registration_number);

          if (isActive) {
            setCourses(data.courses || []);
          }
        }
      } catch (requestError) {
        if (isActive) {
          setStatusError(requestError.response?.data?.message || 'Unable to check payment status automatically.');
        }
      }
    };

    const timeoutId = window.setTimeout(pollPaymentStatus, 3000);
    const intervalId = window.setInterval(pollPaymentStatus, 8000);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [paymentCode?.id, paymentCode?.status, registration?.registration_number]);

  return (
    <>
      <Helmet>
        <title>Pay Course Fees - Afrovate Tech Hub</title>
        <meta name="description" content="Pay outstanding Afrovate Tech Hub course fees using your registration ID." />
      </Helmet>

      <section className="bg-primary-dark pt-32 pb-16 text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-8 text-white text-sm">
            <Link to="/" className="flex items-center hover:text-secondary transition-colors">
              <FaHome className="mr-2" />
              Home
            </Link>
            <FaChevronRight className="mx-2 text-xs text-gray-300" />
            <span className="text-secondary font-medium">Pay Course Fees</span>
          </div>
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Pay Course Fees</h1>
            <p className="text-xl text-white text-opacity-90">
              Enter your registration ID to pay for any unpaid courses you selected during registration.
            </p>
          </div>
        </div>
      </section>

      <Section background="white" padding="lg">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={loadRegistrationCourses} className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <Heading level={3} className="mb-4">Find Your Registration</Heading>
            <label htmlFor="registrationNumber" className="block mb-2 text-sm font-medium text-gray-800">
              Registration ID
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
              <input
                id="registrationNumber"
                value={registrationNumber}
                onChange={(event) => setRegistrationNumber(event.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="AFV-00005"
              />
              <Button
                type="submit"
                variant="primary"
                disabled={loadingRegistration}
                icon={<FaSearch />}
              >
                {loadingRegistration ? 'Checking...' : 'Find Courses'}
              </Button>
            </div>
            {error && <p className="text-red-600 mt-3">{error}</p>}
          </form>

          {registration && (
            <div className="mb-8">
              <div className="bg-secondary bg-opacity-10 border border-secondary border-opacity-40 rounded-lg p-5 mb-6">
                <p className="text-sm text-gray-600">Registration</p>
                <p className="text-2xl font-bold text-primary-dark">{registration.registration_number}</p>
                <p className="text-gray-700">{registration.full_name}</p>
              </div>

              <Heading level={3} className="mb-4">Unpaid Courses</Heading>
              {unpaidCourses.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-green-800">
                  All selected courses are paid or already have active payment codes.
                </div>
              ) : (
                <div className="space-y-3 mb-6">
                  {unpaidCourses.map((course) => {
                    const checked = selectedCourseIds.includes(course.id);

                    return (
                      <label
                        key={course.id}
                        className={`flex items-start rounded-lg border p-4 transition-colors ${
                          checked ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-200 bg-white'
                        } cursor-pointer hover:border-primary`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCourse(course.id)}
                          className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <span className="ml-3 flex-1">
                          <span className="block font-medium text-gray-800">{course.title}</span>
                          <span className="block text-sm text-gray-500">Training fee: {formatMoney(course.amount)}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {courses.some((course) => course.status !== 'unpaid') && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <p className="font-semibold text-gray-800 mb-2">Other selected courses</p>
                  <div className="space-y-2">
                    {courses.filter((course) => course.status !== 'unpaid').map((course) => (
                      <div key={course.id} className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-gray-700">{course.title}</span>
                        <span className={`font-semibold ${course.status === 'paid' ? 'text-green-700' : 'text-primary'}`}>
                          {course.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {unpaidCourses.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
                  <div className="flex items-center justify-between text-lg font-bold text-primary-dark">
                    <span>Total selected</span>
                    <span>{formatMoney(selectedTotal)}</span>
                  </div>
                </div>
              )}

              {statusError && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-4 mb-6">
                  {statusError}
                </div>
              )}

              {paymentCode ? (
                <div className={`${paymentConfirmed ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-6`}>
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className={`${paymentConfirmed ? 'text-green-600' : 'text-primary'} text-2xl mt-1`} />
                    <div className="flex-1">
                      <h4 className={`font-bold mb-2 ${paymentConfirmed ? 'text-green-800' : 'text-primary-dark'}`}>
                        {paymentConfirmed ? 'Payment confirmed' : 'Payment code created'}
                      </h4>
                      <p className="text-gray-700 mb-3">
                        {paymentConfirmed
                          ? 'Your course payment has been confirmed.'
                          : 'Use the USSD code below to complete payment. This page will update automatically after payment.'}
                      </p>
                      <div className="bg-white border border-blue-200 rounded-md p-4 mb-4">
                        <p className="text-sm text-gray-500">USSD Code</p>
                        <p className="text-2xl font-bold text-primary-dark break-all">
                          {paymentCode.ussd_code || 'Code pending'}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                        <p><span className="font-semibold">Status:</span> {paymentStatus}</p>
                        <p><span className="font-semibold">Reference:</span> {paymentCode.reference}</p>
                        <p><span className="font-semibold">Amount:</span> {formatMoney(paymentCode.amount)}</p>
                        {paymentCode.expire_time && (
                          <p><span className="font-semibold">Expires:</span> {new Date(paymentCode.expire_time).toLocaleString()}</p>
                        )}
                      </div>
                      {!paymentConfirmed && !STOP_POLLING_PAYMENT_STATUSES.includes(paymentStatus) && (
                        <p className="text-sm text-gray-600 mt-4">Checking payment status automatically...</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                unpaidCourses.length > 0 && (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCreatePaymentCode}
                    disabled={creatingPaymentCode || selectedCourseIds.length === 0}
                    icon={<FaMoneyBillWave />}
                  >
                    {creatingPaymentCode ? 'Creating Payment Code...' : 'Generate Payment Code'}
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </Section>
    </>
  );
};

export default CoursePaymentPage;
